import { ReactNode, useState } from 'react'
import {
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  styled,
} from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import MenuIcon from '@mui/icons-material/Menu'
import { AccountCircle } from '@mui/icons-material'
import logo from '../../../assets/vounotripidis.logo.png'
import LayoutMenuItem from './LayoutMenuItem'
import layoutMenu from './LayoutMenu'
import { t } from '../../i18n/services/i18n-translation-with-default'

const drawerWidth = 300

const Main = styled('main', {
  shouldForwardProp: (prop: string) => prop !== 'open',
})<{
  open?: boolean
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}))

interface Props {
  children: ReactNode
}

const Layout = (props: Props) => {
  const [open, setOpen] = useState(true)
  const { home, users, lessons, vehicles, calendar, settings } = layoutMenu(t)

  const toggleDrawer = () => {
    setOpen(!open)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {t('app.title')}
          </Typography>
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={false}
            >
              <MenuItem>Profile</MenuItem>
              <MenuItem>My account</MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <div style={{ position: 'absolute', left: 0 }}>
            <img
              src={logo}
              alt="logo"
              style={{ width: '280px', padding: '10px' }}
            />
          </div>
        </DrawerHeader>
        <Divider />
        <LayoutMenuItem menu={home.menu} submenu={home.submenu} />
        <Divider />
        <LayoutMenuItem menu={users.menu} submenu={users.submenu} />
        <Divider />
        <LayoutMenuItem menu={lessons.menu} submenu={lessons.submenu} />
        <Divider />
        <LayoutMenuItem menu={vehicles.menu} submenu={vehicles.submenu} />
        <Divider />
        <LayoutMenuItem menu={calendar.menu} submenu={calendar.submenu} />
        <Divider />
        <LayoutMenuItem menu={settings.menu} submenu={settings.submenu} />
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        <Box sx={{ marginTop: '30px' }}>{props.children}</Box>
      </Main>
    </Box>
  )
}

export default Layout
